import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import EssayQuestion from './EssayQuestion';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import ProgressBar from './ProgressBar';
import SingleQuestion from './SingleQuestion';
import StepMessage from './StepMessage';
import { getFirstUnansweredQuestionIndex } from './utils';
import csrfObject from '../../modules/csrf';

const propTypes = {
  allowMessaging: PropTypes.bool.isRequired,
  answerEndpoint: PropTypes.string.isRequired,
  courseDir: PropTypes.string,
  courseId: PropTypes.number.isRequired,
  dir: PropTypes.string,
  markCompleteEndpoint: PropTypes.string.isRequired,
  messageEndpoint: PropTypes.string.isRequired,
  questionsEndpoint: PropTypes.string.isRequired,
  strings: PropTypes.shape({
    allQuestionsAnswered: PropTypes.string,
    cancel: PropTypes.string,
    continue: PropTypes.string,
    placeholder: PropTypes.string,
    submit: PropTypes.string,
    successMessage: PropTypes.string,
    thoughtQuestion: PropTypes.string,
    trigger: PropTypes.string,
    tryAgain: PropTypes.string,
  }).isRequired,
};

const StepQuestions = ({
  allowMessaging,
  answerEndpoint,
  courseDir = null,
  courseId,
  dir = null,
  markCompleteEndpoint,
  messageEndpoint,
  questionsEndpoint,
  strings,
}) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);

  const activeQuestion = questions[activeQuestionIndex];
  const allQuestionsAnswered = loaded && questions.filter(q => !q.answered).length === 0;
  const isLastQuestion = loaded && activeQuestionIndex === (questions.length - 1);

  const goToNextUrl = () => {
    $.post(markCompleteEndpoint, {
      ...csrfObject(),
    }).done(({ nextUrl }) => {
      window.location = nextUrl;
    });
  };

  const proceed = () => {
    if (isLastQuestion) {
      goToNextUrl();
      return;
    }

    setActiveQuestionIndex(oldValue => oldValue + 1);
    setMessage(null);
  };

  const handleSubmit = (questionId, { selectedOptionIds = [''], text = '' }) => {
    if (submitting) {
      return;
    }

    const req = $.post(answerEndpoint, {
      ...csrfObject(),
      question_id: questionId,
      answer_text: text,
      question_option_ids: selectedOptionIds,
    });

    setSubmitting(true);

    req.done((data, _, { status }) => {
      if (status !== 201) {
        // eslint-disable-next-line
        alert('There was a problem submitting your answer. Please try again.');
        return;
      }

      if (!data.message) {
        proceed();
        return;
      }

      setLastAnswerCorrect(data.correct);
      setMessage(data.message);
    });

    req.always(() => {
      setSubmitting(false);
    });
  };

  const handleTryAgainClick = () => {
    setLastAnswerCorrect(null);
    setMessage(null);
  };

  const activeQuestionComponent = () => {
    switch (activeQuestion.kind) {
      case 'single':
        return (
          <SingleQuestion
            onSubmit={handleSubmit}
            question={activeQuestion}
            dir={courseDir}
          />
        );

      case 'essay':
        return (
          <EssayQuestion
            onSubmit={handleSubmit}
            question={activeQuestion}
            strings={strings}
            dir={courseDir}
          />
        );
      case 'multiple':
        return (
          <MultipleChoiceQuestion
            onSubmit={handleSubmit}
            question={activeQuestion}
            strings={strings}
            dir={courseDir}
          />
        );

      default:
        // eslint-disable-next-line
        console.error(`Unexpected question type: ${question.kind}`);
        return null;
    }
  };

  const responseMessageComponent = () => (
    <div className="question question--message">
      <p dir={courseDir} data-test="questionMessage">{message}</p>

      <div className="question__answers" dir={dir}>
        <ul className="list-unstyled">
          {!lastAnswerCorrect && (
            <li>
              <button
                className="question__answer question__btn"
                onClick={handleTryAgainClick}
                type="button"
              >
                {strings.tryAgain}
              </button>
            </li>
          )}
          <li>
            <button
              className="question__answer question__btn"
              data-test="questionContinueButton"
              onClick={proceed}
              type="button"
            >
              {strings.continue}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );

  useEffect(() => {
    const req = $.get(questionsEndpoint);

    req.done(newQuestions => {
      setActiveQuestionIndex(getFirstUnansweredQuestionIndex(newQuestions));
      setLoaded(true);
      setQuestions(newQuestions);
    });
  }, []);

  if (!loaded) {
    return null;
  }

  if (allQuestionsAnswered) {
    return (
      <div className="lesson-questions">
        <h2 className="lesson-questions__heading" dir={dir}>
          {strings.allQuestionsAnswered}
        </h2>

        <br />

        <div className="question question--message">
          <div className="question__answers">
            <ul className="list-unstyled">
              <li>
                <button
                  className="question__btn"
                  onClick={goToNextUrl}
                  type="button"
                >
                  {strings.continue}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="lesson-questions">
        <h2 className="lesson-questions__heading" dir={dir}>
          {strings.thoughtQuestion}
        </h2>

        {questions.length > 1 && (
          <ProgressBar
            activeIndex={activeQuestionIndex}
            questions={questions}
          />
        )}

        {message && responseMessageComponent()}
        {!message && activeQuestionComponent()}
      </div>
      {allowMessaging && (
        <StepMessage
          courseId={courseId}
          dir={dir}
          endpoint={messageEndpoint}
          strings={({
            buttonCancel: strings.cancel,
            buttonSubmit: strings.submit,
            placeholder: strings.placeholder,
            successMessage: strings.successMessage,
            trigger: strings.trigger,
          })}
          questionId={activeQuestion.id}
        />
      )}
    </>
  );
};

StepQuestions.propTypes = propTypes;

export default StepQuestions;
